/**
 * usePropertyList – list data, filters, pagination, sorting
 * Session key: propertylist_filter (matches Angular)
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { getPropertyList } from '../services/propertyService'
import type { Property, PropertyFilters, PropertyListResponse } from '../types/property'
import { normalizePmsPropertyCodes } from '../utils/propertyListLogic'
import { useUserRole } from '@/hooks/useUserRole'
import {
  PROPERTYLIST_FILTER_SESSION_KEY,
  DEBOUNCED_FILTER_KEYS,
  DEBOUNCE_MS,
  META_KEYS,
} from '../constants'

/**
 * Badge count: only filters the user changed beyond cold-load defaults (Liberty org_id=1 + listed=Active are not "extra").
 */
function countActiveFiltersVsBaseline(
  filters: PropertyFilters,
  baseline: PropertyFilters
): number {
  let n = 0
  for (const key of Object.keys(filters) as (keyof PropertyFilters)[]) {
    if (META_KEYS.has(key as string)) continue
    const value = filters[key]
    if (value === undefined || value === null) continue
    if (value === '') continue
    if (Array.isArray(value) && value.length === 0) continue
    if (key === 'check_for_delete') {
      if (value === true || value === '1' || value === 1) n++
      continue
    }
    const baseVal = baseline[key]
    if (baseVal !== undefined && String(value) === String(baseVal)) continue
    n++
  }
  return n
}

function isAbortError(err: unknown): boolean {
  return (
    (typeof err === 'object' &&
      err !== null &&
      'code' in err &&
      (err as { code?: string }).code === 'ERR_CANCELED') ||
    (err instanceof DOMException && err.name === 'AbortError')
  )
}

export function usePropertyList() {
  const { isLibertyRole, orgId, companyId, companyName } = useUserRole()
  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const listAbortRef = useRef<AbortController | null>(null)

  /** Cold load (no session): Liberty defaults listed to Active (`'1'`). See getInitialFilters for company→Properties path. */
  const defaultFilters: PropertyFilters = useMemo(
    () => ({
      page: 1,
      perPage: 15,
      sortBy: 'asc',
      orderBy: 'property_name',
      org_id: isLibertyRole ? 1 : (orgId || ''),
      listed: isLibertyRole ? '1' : '',
      // Angular getAccessiblePermission: role with company_id locks + seeds company filter.
      ...(companyId
        ? {
            company_id: companyId,
            company_name: (companyName && String(companyName).trim()) || undefined,
          }
        : {}),
    }),
    [isLibertyRole, orgId, companyId, companyName],
  )

  /** Keep role-scoped company on the filter even if UI/session tries to clear it. */
  const withLockedCompany = useCallback(
    (filters: PropertyFilters): PropertyFilters => {
      if (!companyId) return filters
      return {
        ...filters,
        company_id: companyId,
        company_name:
          (companyName && String(companyName).trim()) ||
          (filters.company_name && String(filters.company_name).trim()) ||
          undefined,
      }
    },
    [companyId, companyName],
  )

  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [currentFilters, setCurrentFilters] = useState<PropertyFilters | null>(null)
  const currentFiltersRef = useRef<PropertyFilters | null>(null)

  const updateCurrentFilters = useCallback((filters: PropertyFilters | null) => {
    currentFiltersRef.current = filters
    setCurrentFilters(filters)
  }, [])

  const saveFiltersToSession = useCallback((filters: PropertyFilters) => {
    try {
      sessionStorage.setItem(PROPERTYLIST_FILTER_SESSION_KEY, JSON.stringify(filters))
    } catch (e) {
      console.error('Failed to save property filters to session:', e)
    }
  }, [])

  const clearFiltersFromSession = useCallback(() => {
    try {
      sessionStorage.removeItem(PROPERTYLIST_FILTER_SESSION_KEY)
    } catch (e) {
      console.error('Failed to clear property filters from session:', e)
    }
  }, [])

  /**
   * Angular `setDefaultRequestParam`: `listed` is `'1'` only when there is no `propertylist_filter`.
   * When session exists (e.g. Company edit → Properties), `listed` defaults to `''` (All) unless session sets it.
   */
  const getInitialFilters = useCallback((): PropertyFilters => {
    try {
      const raw = sessionStorage.getItem(PROPERTYLIST_FILTER_SESSION_KEY)
      if (!raw) return withLockedCompany(defaultFilters)

      const sessionParsed = JSON.parse(raw) as Partial<PropertyFilters>
      const hasSession =
        sessionParsed &&
        typeof sessionParsed === 'object' &&
        Object.keys(sessionParsed).length > 0

      if (!hasSession) return withLockedCompany(defaultFilters)

      return withLockedCompany({
        ...defaultFilters,
        ...sessionParsed,
        listed:
          'listed' in sessionParsed && sessionParsed.listed !== undefined
            ? (sessionParsed.listed as string)
            : '',
        pms_property_code: normalizePmsPropertyCodes(sessionParsed.pms_property_code),
      })
    } catch (e) {
      console.error('Error loading property filters:', e)
      return withLockedCompany(defaultFilters)
    }
  }, [defaultFilters, withLockedCompany])

  const fetchProperties = useCallback(
    async (filters: PropertyFilters, showLoading = true) => {
      listAbortRef.current?.abort()
      const abort = new AbortController()
      listAbortRef.current = abort

      if (showLoading) setLoading(true)
      setError(null)
      try {
        const response: PropertyListResponse = await getPropertyList(
          filters,
          abort.signal,
        )
        if (abort.signal.aborted) return
        setProperties(response.data || [])
        setPagination({
          current: response.current || 1,
          last: response.last || 1,
          total: response.total || 0,
        })
      } catch (err) {
        if (abort.signal.aborted || isAbortError(err)) return
        setError(
          err instanceof Error ? err.message : 'Failed to fetch properties'
        )
        // Angular keeps the prior list on failure — do not clear rows.
      } finally {
        if (!abort.signal.aborted && showLoading) setLoading(false)
      }
    },
    []
  )

  const applyFilters = useCallback(
    (newFilters: Partial<PropertyFilters>) => {
      const base = currentFiltersRef.current || getInitialFilters()
      const isOnlyPage =
        Object.keys(newFilters).length === 1 && 'page' in newFilters
      const pageValue =
        newFilters.page !== undefined
          ? newFilters.page
          : isOnlyPage
            ? base.page
            : 1
      const updated = withLockedCompany({ ...base, ...newFilters, page: pageValue })
      updateCurrentFilters(updated)
      saveFiltersToSession(updated)

      const changedKeys = Object.keys(newFilters) as (keyof PropertyFilters)[]
      const onlyDebouncedKeys =
        changedKeys.length > 0 &&
        changedKeys.every((k) =>
          (DEBOUNCED_FILTER_KEYS as readonly string[]).includes(k as string)
        )

      if (onlyDebouncedKeys) {
        if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => {
          fetchDebounceRef.current = null
          fetchProperties(updated)
        }, DEBOUNCE_MS)
      } else {
        if (fetchDebounceRef.current) {
          clearTimeout(fetchDebounceRef.current)
          fetchDebounceRef.current = null
        }
        fetchProperties(updated)
      }
    },
    [getInitialFilters, saveFiltersToSession, fetchProperties, updateCurrentFilters, withLockedCompany]
  )

  const resetFilters = useCallback(() => {
    if (fetchDebounceRef.current) {
      clearTimeout(fetchDebounceRef.current)
      fetchDebounceRef.current = null
    }
    clearFiltersFromSession()
    const next = withLockedCompany(defaultFilters)
    updateCurrentFilters(next)
    fetchProperties(next)
  }, [
    clearFiltersFromSession,
    fetchProperties,
    updateCurrentFilters,
    defaultFilters,
    withLockedCompany,
  ])

  const changePage = useCallback(
    (page: number) => applyFilters({ page }),
    [applyFilters]
  )

  const changePerPage = useCallback(
    (perPage: number) => applyFilters({ perPage, page: 1 }),
    [applyFilters]
  )

  const sort = useCallback(
    (orderBy: string) => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = null
      }
      const base = currentFiltersRef.current || getInitialFilters()
      // Match Angular: every header click toggles asc ↔ desc, including when switching columns.
      // Angular `sort` → `triggerFilter` resets page to 1.
      const newSortBy = base.sortBy === 'asc' ? 'desc' : 'asc'
      const updated: PropertyFilters = {
        ...base,
        orderBy,
        sortBy: newSortBy,
        page: 1,
      }
      updateCurrentFilters(updated)
      saveFiltersToSession(updated)
      fetchProperties(updated)
    },
    [getInitialFilters, saveFiltersToSession, fetchProperties, updateCurrentFilters]
  )

  useEffect(() => {
    const filters = getInitialFilters()
    updateCurrentFilters(filters)
    fetchProperties(filters)
  }, [])

  useEffect(() => {
    return () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = null
      }
      listAbortRef.current?.abort()
    }
  }, [])

  const getEffectiveFilters = useCallback((): PropertyFilters => {
    return currentFiltersRef.current || currentFilters || getInitialFilters()
  }, [currentFilters, getInitialFilters])

  const activeFiltersCount = useMemo(() => {
    const f = currentFiltersRef.current ?? currentFilters ?? getInitialFilters()
    return countActiveFiltersVsBaseline(f, defaultFilters)
  }, [currentFilters, defaultFilters, getInitialFilters])

  return {
    properties,
    loading,
    error,
    pagination,
    filters: getEffectiveFilters(),
    activeFiltersCount,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch: () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = null
      }
      fetchProperties(getEffectiveFilters())
    },
  }
}
